import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Wind, Droplets, Thermometer, Activity, Wifi, MapPin, Plus, LogOut, ChevronRight, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import logger from '@/utils/logger';

function getAqiLevel(aqi) {
  if (aqi <= 50) return { label: 'Good', color: 'var(--aqi-good)', bg: '#D1FAE5', textColor: '#065f46' };
  if (aqi <= 100) return { label: 'Moderate', color: 'var(--aqi-moderate)', bg: '#FEF3C7', textColor: '#92400e' };
  if (aqi <= 150) return { label: 'Unhealthy for Sensitive', color: '#F97316', bg: '#FFEDD5', textColor: '#9a3412' };
  if (aqi <= 200) return { label: 'Unhealthy', color: 'var(--aqi-unhealthy)', bg: '#FFE4E6', textColor: '#9f1239' };
  return { label: 'Hazardous', color: 'var(--aqi-hazardous)', bg: '#FEE2E2', textColor: '#7f1d1d' };
}

function AqiGauge({ value, size = 'large' }) {
  const level = getAqiLevel(value || 0);
  const isLarge = size === 'large';
  return (
    <div className="flex flex-col items-center">
      <div
        className={`rounded-full flex items-center justify-center aqi-pulse ${isLarge ? 'w-32 h-32' : 'w-20 h-20'}`}
        style={{ background: level.bg, border: `3px solid ${level.color}` }}
        data-testid="aqi-gauge"
      >
        <span className={`font-bold ${isLarge ? 'text-4xl' : 'text-2xl'}`} style={{ color: level.color, fontFamily: 'Outfit' }}>
          {value || '--'}
        </span>
      </div>
      <span
        className={`mt-2 font-semibold ${isLarge ? 'text-sm' : 'text-xs'} px-3 py-1 rounded-full`}
        style={{ background: level.bg, color: level.textColor }}
        data-testid="aqi-label"
      >
        {level.label}
      </span>
    </div>
  );
}

// Memoized chart style constants
const TICK_STYLE = { fontSize: 10, fill: 'var(--text-muted)' };
const TOOLTIP_STYLE = { borderRadius: 12, border: '1px solid var(--border-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontFamily: 'Manrope', fontSize: 12 };
const TOOLTIP_STYLE_SIMPLE = { borderRadius: 12, border: '1px solid var(--border-color)', fontFamily: 'Manrope', fontSize: 12 };
const BAR_RADIUS = [4, 4, 0, 0];
const CHART_CONTAINER_STYLE = { width: '100%', height: 224, minHeight: 224 };

const formatTime = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit' });
const formatLabel = (t) => new Date(t).toLocaleString();

const POLLUTANTS = [
  { label: 'CO', key: 'co', unit: 'ug/m3', icon: Activity },
  { label: 'NO2', key: 'no2', unit: 'ug/m3', icon: Droplets },
  { label: 'SO2', key: 'so2', unit: 'ug/m3', icon: Wind },
  { label: 'O3', key: 'o3', unit: 'ug/m3', icon: Thermometer },
];

export default function Dashboard() {
  const { user, logout, api } = useAuth();
  const [currentAqi, setCurrentAqi] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [devices, setDevices] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [aqiRes, forecastRes, devicesRes, citiesRes] = await Promise.all([
        api.get('/aqi/current?lat=19.0760&lon=72.8777'),
        api.get('/aqi/forecast?lat=19.0760&lon=72.8777&days=2'),
        api.get('/devices'),
        api.get('/aqi/cities'),
      ]);
      setCurrentAqi(aqiRes.data);
      const hourlyData = forecastRes.data.data || [];
      setForecast(hourlyData.filter((_, i) => i % 3 === 0).slice(0, 24));
      setDevices(devicesRes.data.devices || []);
      setCities(citiesRes.data.cities || []);
    } catch (e) {
      logger.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const barChartData = useMemo(() => forecast.slice(0, 12), [forecast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--page-bg)' }}>
        <div className="flex flex-col items-center gap-4">
          <Wind className="w-10 h-10 animate-spin" style={{ color: 'var(--brand-sky)' }} />
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }}>
      <header className="glass-header sticky top-0 z-50 px-4 md:px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo-link">
            <Wind className="w-6 h-6" style={{ color: 'var(--brand-sky)' }} strokeWidth={1.5} />
            <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: 'var(--text-primary)' }}>Vayu Sense</span>
          </Link>
          <div className="flex items-center gap-4">
            <button onClick={handleRefresh} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="refresh-dashboard-btn" title="Refresh data">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} style={{ color: 'var(--text-secondary)' }} />
            </button>
            <span className="text-sm hidden md:block" style={{ color: 'var(--text-secondary)' }}>{user?.name || user?.email}</span>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors hover:bg-red-50" style={{ color: '#DC2626', fontFamily: 'Outfit' }} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <div className="mb-8 animate-fade-in-up">
          <h1 className="text-2xl md:text-3xl font-medium" style={{ fontFamily: 'Outfit', color: 'var(--text-primary)' }} data-testid="dashboard-welcome">Welcome, {user?.name || 'User'}</h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)', fontFamily: 'Manrope', fontSize: '0.95rem' }}>Here's your air quality overview for today</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="card-elevated p-6 lg:p-8 animate-fade-in-up delay-100" data-testid="main-aqi-card">
            <div className="overline mb-4">Current Air Quality - Mumbai</div>
            <div className="flex items-center justify-between">
              <AqiGauge value={currentAqi?.aqi} />
              <div className="space-y-3 text-right">
                {[{k:'pm25',l:'PM2.5'},{k:'pm10',l:'PM10'},{k:'o3',l:'O3'}].map(({k,l})=>(
                  <div key={k}>
                    <span className="text-xs block" style={{ color: 'var(--text-muted)' }}>{l}</span>
                    <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>{currentAqi?.[k]?.toFixed(1) || '--'}</span>
                    <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>ug/m3</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-elevated p-6 lg:col-span-2 animate-fade-in-up delay-200" data-testid="pollutant-breakdown-card">
            <div className="overline mb-4">Pollutant Breakdown</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {POLLUTANTS.map((item) => (
                <div key={item.label} className="p-4 rounded-xl" style={{ background: 'var(--subtle-bg)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="w-4 h-4" style={{ color: 'var(--brand-sky)' }} strokeWidth={1.5} />
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                  </div>
                  <span className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>{currentAqi?.[item.key]?.toFixed(1) || '--'}</span>
                  <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>{item.unit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="card-elevated p-6 animate-fade-in-up delay-300" data-testid="aqi-trend-chart">
            <div className="overline mb-4">AQI Trend (48h Forecast)</div>
            <div style={CHART_CONTAINER_STYLE}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={forecast}>
                  <defs>
                    <linearGradient id="aqiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand-sky)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="var(--brand-sky)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" tickFormatter={formatTime} tick={TICK_STYLE} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={formatLabel} />
                  <Area type="monotone" dataKey="aqi" stroke="var(--brand-sky)" fill="url(#aqiGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-elevated p-6 animate-fade-in-up delay-400" data-testid="pm-chart">
            <div className="overline mb-4">PM2.5 vs PM10</div>
            <div style={CHART_CONTAINER_STYLE}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData}>
                  <XAxis dataKey="time" tickFormatter={formatTime} tick={TICK_STYLE} axisLine={false} tickLine={false} />
                  <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE_SIMPLE} />
                  <Bar dataKey="pm25" fill="var(--brand-sky)" radius={BAR_RADIUS} name="PM2.5" />
                  <Bar dataKey="pm10" fill="var(--brand-sage)" radius={BAR_RADIUS} name="PM10" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card-elevated p-6 animate-fade-in-up delay-500" data-testid="devices-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Your Devices</div>
              <button className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-colors" style={{ background: 'var(--brand-sage-light)', color: '#065f46' }} data-testid="add-device-btn">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {devices.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }}>No devices connected yet</p>
            ) : (
              <div className="space-y-3">
                {devices.map((device) => (
                  <div key={device.id} className="flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-slate-50" style={{ border: '1px solid var(--border-color)' }} data-testid={`device-item-${device.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: device.device_type === 'outdoor' ? '#DBEAFE' : 'var(--brand-sage-light)' }}>
                        {device.device_type === 'outdoor' ? <Wind className="w-4 h-4" style={{ color: 'var(--brand-sky)' }} strokeWidth={1.5} /> : <Wifi className="w-4 h-4" style={{ color: 'var(--brand-sage)' }} strokeWidth={1.5} />}
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ fontFamily: 'Outfit' }}>{device.name}</p>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{device.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: getAqiLevel(device.last_reading?.aqi || 0).color }}>{device.last_reading?.aqi || '--'}</span>
                      <span className="text-xs block" style={{ color: 'var(--text-muted)' }}>AQI</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-elevated p-6 animate-fade-in-up delay-600" data-testid="cities-aqi-panel">
            <div className="overline mb-4">City Air Quality Index</div>
            <div className="space-y-2">
              {cities.map((city) => {
                const level = getAqiLevel(city.aqi || 0);
                return (
                  <div key={city.name} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`city-item-${city.name}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ background: level.color }} />
                      <span className="text-sm font-medium" style={{ fontFamily: 'Outfit' }}>{city.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: level.bg, color: level.textColor }}>{level.label}</span>
                      <span className="text-sm font-semibold w-8 text-right" style={{ fontFamily: 'Outfit', color: level.color }}>{city.aqi || '--'}</span>
                      <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
